import { useTranslation } from 'react-i18next'
import { useGetFile } from 'src/api/file'
import { AppForm, FormImageFileUpload, FormTextField } from 'src/components/FormCore'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { MainButton } from 'src/components/MainButton'
import { Spinner } from 'src/components/Spinner'
import { useZodForm } from 'src/hooks/zod-form'
import { ShowDialogProps } from 'src/show-dialog'
import { z } from 'zod'

export interface StorageFormDialogProps extends ShowDialogProps {
  customerId: number
  initialValues?: any
  isLoading: boolean
  onSubmit: (form: any, pop: (...args: any[]) => any) => void
}

const StorageFormDialog = ({ customerId, pop, initialValues, isLoading, onSubmit }: StorageFormDialogProps) => {
  const { t } = useTranslation('storage')
  const { data: Image } = useGetFile(!initialValues?.image ? (initialValues?.imageId as number) : undefined)

  const schema = z.object({
    name: z.string().nonempty(t('nameIsRequired', { ns: 'service-station' })),
    address: z.string().nonempty(t('addressIsRequired', { ns: 'storage' })),
    imageId: z.number().or(z.null()).optional(),
  })

  const form = useZodForm(schema, {
    defaultValues: {
      ...initialValues,
      ...{ customerId },
    },
  })

  const onSubmitHandle = async (formData: any) => {
    await onSubmit(formData, pop)
  }

  return (
    <GenericDialogLayout
      title={`${initialValues ? t('update', { ns: 'common' }) : t('add', { ns: 'common' })} ${t('storage')}`}
      pop={pop}
      actions={[
        <MainButton
          key="submit"
          onClick={() => {
            form.handleSubmit(onSubmitHandle)()
          }}
        >
          {isLoading && <Spinner size={20} />}
          {!initialValues ? t('create', { ns: 'common' }) : t('update', { ns: 'common' })}
        </MainButton>,
        <MainButton key="cancel" onClick={pop} variant="outlined">
          {t('cancel', { ns: 'common' })}
        </MainButton>,
      ]}
    >
      <AppForm form={form} hasInitialValues={!!initialValues}>
        <FormTextField name="name" label={t('name', { ns: 'common' })} />
        <FormTextField name="address" label={t('address', { ns: 'common' })} />
        <FormImageFileUpload
          label={t('storage-image')}
          name="imageId"
          mapFileKey="id"
          initialFiles={initialValues?.image ?? Image}
          inactiveMsg={t('chooseCertificate', { ns: 'locality' })}
        />
      </AppForm>
    </GenericDialogLayout>
  )
}

export default StorageFormDialog
